import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomerApiService } from '../../../services/customer-api.service';
import { LookupApiService } from '../../../services/lookup-api.service';

export interface AddChildDialogData {
  masterId: number;
  masterName: string;
}

@Component({
  selector: 'app-add-child-dialog',
  templateUrl: './add-child-dialog.component.html',
  styleUrls: ['./add-child-dialog.component.css'],
})
export class AddChildDialogComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  saving = false;
  error = '';
  states: { id: number; code: string; label: string }[] = [];

  private destroyed$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private api: CustomerApiService,
    private lookups: LookupApiService,
    private dialogRef: MatDialogRef<AddChildDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddChildDialogData,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      displayName: ['', [Validators.required, Validators.maxLength(128)]],
      serviceAddress: this.fb.group({
        line1: ['', [Validators.required, Validators.maxLength(100)]],
        line2: ['', Validators.maxLength(100)],
        city:  ['', [Validators.required, Validators.maxLength(64)]],
        stateId: [null as number | null, Validators.required],
        zip:   ['', [Validators.required, Validators.maxLength(12)]],
      }),
      differentBilling: [false],
      billingAddress: this.fb.group({
        line1: ['', Validators.maxLength(100)],
        line2: ['', Validators.maxLength(100)],
        city:  ['', Validators.maxLength(64)],
        stateId: [null as number | null],
        zip:   ['', Validators.maxLength(12)],
      }),
    });
    this.lookups.states()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({ next: states => { this.states = states ?? []; }, error: () => {} });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get differentBilling(): boolean {
    return this.form.get('differentBilling')!.value;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const svc = this.form.get('serviceAddress')!.value;
    const bil = this.form.get('billingAddress')!.value;

    this.api.addChild(this.data.masterId, {
      displayName: this.form.get('displayName')!.value,
      serviceAddress: {
        streetAddressLine1: svc.line1 || null,
        streetAddressLine2: svc.line2 || null,
        city: svc.city || null,
        stateId: svc.stateId ?? null,
        postalCode: svc.zip || null,
      },
      billingSameAsService: !this.differentBilling,
      billingAddress: this.differentBilling ? {
        streetAddressLine1: bil.line1 || null,
        streetAddressLine2: bil.line2 || null,
        city: bil.city || null,
        stateId: bil.stateId ?? null,
        postalCode: bil.zip || null,
      } : null,
    }).pipe(takeUntil(this.destroyed$)).subscribe({
      next: result => {
        this.saving = false;
        this.dialogRef.close(result);
      },
      error: (e: any) => {
        this.saving = false;
        this.error = e?.error?.error ?? e?.error?.message ?? 'Failed to create child account.';
      },
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
